import { FlightsClassTypes } from 'constants/flight'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Modal from 'LuxKit/Modal/Modal'
import React, { useCallback, useState } from 'react'
import FareClassCard from './FareClassCard'
import Caption from 'LuxKit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Breakpoint from 'components/Common/Breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { useFareClassPriceDifferencesPerAdult } from 'hooks/CharterFlights/useFareClassPriceDifferences'
import { EmptyArray } from 'lib/array/arrayUtils'

const imageIds = {
  [FlightsClassTypes.ECONOMY]: 'h39orcxvpb4p6e5hbig',
  [FlightsClassTypes.PREMIUM]: '8qew3qgqjf2w2z26wf1',
  [FlightsClassTypes.BUSINESS]: '8jlsvhb4ztgfiviaiuja',
} as const

interface Props {
  departDate?: string
  returnDate?: string
  occupancies?: Array<App.Occupants>
  selectedClassId: FlightsClassTypes
}

/**
 * This is specific to the Maldives charter flights. It's not designed to be reusable for future charter flight offers.
 * Uses modal's resolve mechanism to return the selected fare class.
 */
function MaldivesCharterFlightFareClassModal({ selectedClassId, departDate, returnDate, occupancies = EmptyArray }: Props) {
  const modalElement = useModalElementContext<FlightsClassTypes>()

  // Use local state to track selection within the modal
  const [localSelectedClass, setLocalSelectedClass] = useState<FlightsClassTypes>(selectedClassId)

  const onClassSelect = useCallback((fareClass: FlightsClassTypes) => {
    setLocalSelectedClass(fareClass)
  }, [])

  const closeModal = useCallback(() => {
    // Return the selected class via modal's resolve mechanism
    modalElement.resolve(localSelectedClass)
  }, [modalElement, localSelectedClass])

  const priceDifferences = useFareClassPriceDifferencesPerAdult({
    charterBundleRoute: 'melbourne-maldives',
    baseClass: FlightsClassTypes.ECONOMY,
    comparisonClasses: [FlightsClassTypes.BUSINESS],
    departDate,
    returnDate,
    occupancies,
    selectedFareClass: selectedClassId,
  })

  const businessExtraCost = priceDifferences[FlightsClassTypes.BUSINESS]

  const cards = <>
    <FareClassCard
      fareId={FlightsClassTypes.ECONOMY}
      imageId={imageIds[FlightsClassTypes.ECONOMY]}
      name="Economy"
      subtitle={<Caption
        variant="large"
        weight="normal"
        colour="neutral-two"
      >
        Included in bundle
      </Caption>}
      isSelected={localSelectedClass === FlightsClassTypes.ECONOMY}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>Seating with modest legroom.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Personal inflight entertainment screens.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Complimentary meal and beverage service.</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>7kg carry-on</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>23kg checked bag</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard>

    <FareClassCard
      fareId={FlightsClassTypes.BUSINESS}
      imageId={imageIds[FlightsClassTypes.BUSINESS]}
      name="Business"
      subtitle={!!businessExtraCost && <Caption
        variant="large"
        weight="normal"
        colour="neutral-two"
      >
        <Group direction="horizontal" gap={4} verticalAlign="baseline">
          <PriceRowCaption>From</PriceRowCaption>
          <PriceRowPrice price={businessExtraCost} saleUnit="person" size="S" signDisplay="always" />
        </Group>
      </Caption>}
      isSelected={localSelectedClass === FlightsClassTypes.BUSINESS}
      onSelect={onClassSelect}
    >
      <VerticalSpacer gap={16}>
        <VerticalSpacer gap={8}>
          <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>18 Flat-Bed Seats - fully flat, with in-seat power.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>Large screens for on-demand entertainment.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>Gourmet Meal Options - International cuisine and a wide range of beverages.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>Priority boarding.</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineClochIcon />}>Dedicated cabin crew for personalised service.</CaptionTextBlock>
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          <BodyText variant="small" weight="bold">Baggage</BodyText>
          <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>7kg carry-on</CaptionTextBlock>
          <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>40kg checked baggage</CaptionTextBlock>
        </VerticalSpacer>
      </VerticalSpacer>
    </FareClassCard>
  </>

  return <Modal
    title="Select a class"
    subtitle="Maldivian · Airbus A330-300"
    primaryActionText="Continue"
    onPrimaryActionClick={closeModal}
    onClose={closeModal}
    size="XL"
    height="full"
    tabletHeight="auto"
  >
    <Breakpoint max="tablet">
      <CardCarousel gap={12} snap="start" gutterSize={20}>
        {cards}
      </CardCarousel>
    </Breakpoint>

    <Breakpoint min="desktop">
      <Group direction="horizontal" gap={12} verticalAlign="stretch" horizontalAlign="stretch">
        {cards}
      </Group>
    </Breakpoint>
  </Modal>
}

export default MaldivesCharterFlightFareClassModal
